/**
 * @author        woody@zumper.com (Woody Anderson)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   media object
 */

import { Bizo, type Media as MediaBizoType, type RawMedia, autoclass } from '@zcode/bizos'
import { MediaType } from '@zcode/enums'
import { mediaUrl, staticUrl } from '@zcode/nr-utils'
import { constructUrl } from '../packages/imgix'
import {
  selectIframeMaxResImage,
  selectIframeUrl,
  selectInternalVideoImage,
  selectInternalVideoUrl,
  selectVideoMaxishResImage,
  selectVideoUrl,
} from './media/selectors'

export enum Size {
  /** deprecated */
  TINY = '1280x960',
  /** deprecated */
  SMALL = '1280x960',
  /** deprecated */
  MEDIUM = '1280x960',
  /** deprecated */
  LARGE = '1280x960',
  XLARGE = '1280x960',
  FULL = 'full',
}

/** deprecated */
export const SIZE_TINY = Size.TINY
/** deprecated */
export const SIZE_SMALL = Size.SMALL
/** deprecated */
export const SIZE_MEDIUM = Size.MEDIUM
/** deprecated */
export const SIZE_LARGE = Size.LARGE
export const SIZE_XLARGE = Size.XLARGE
export const SIZE_FULL = Size.FULL

export const getImageUrlBySize = (
  args: { id?: number; size?: Size; noImage?: boolean; mediaType?: MediaType } = {}
) => {
  const { id, size = Size.MEDIUM, noImage = false, mediaType = MediaType.IMAGE } = args
  if (mediaType === MediaType.IMAGE || mediaType === MediaType.LAYOUT) {
    if (id) {
      // NOTE: If we are asking for size full, we return a size full
      // We should only use this size only when absolutely necessary.
      if (size === Size.FULL) {
        return constructUrl(mediaUrl(id, Size.FULL), {})
      }

      // if x-large, just return x-large since that's the base url
      if (size === Size.XLARGE) {
        return constructUrl(mediaUrl(id, Size.XLARGE), {})
      }

      // Eventually we want to go away from using this function function and instead specify
      // at the component level, the exact dimension we want for the image.
      // This will be the stop gap solution that will help us reduce the billing from Imgix
      // since Imgix bills us based on the base url.
      // Here, we are standardizing all the images to use '1280x960', or SIZE_XLARGE, as the base.
      // The width and height of each size is based off `zumper/utils/image.py`.
      let w: number | undefined
      let h: number | undefined
      if (size === Size.TINY) {
        w = 68
        h = 47
      } else if (size === Size.SMALL) {
        w = 143
        h = 94
      } else if (size === Size.MEDIUM) {
        w = 360
        h = 240
      } else if (size === Size.LARGE) {
        w = 640
        h = 480
      }

      return constructUrl(mediaUrl(id, Size.XLARGE), { w, h })
    } else if (noImage) {
      return staticUrl('img/nopics.svg')
    }
  }
  return ''
}

export default autoclass<MediaBizoType, RawMedia>(
  'Media',
  class Media extends Bizo<MediaBizoType, RawMedia> {},
  {
    id: 'media_id',
    type: 'media_type',
    mediaId: 'media_id',
    name: 'name',
    originUrl: 'origin_url',
    height: 'height',
    width: 'width',
    isAvailable: 'is_available',
    mediaType: 'media_type',
    mediaUrl: (t: MediaBizoType) => {
      if (t.mediaType === MediaType.VIDEO) {
        return selectVideoUrl(t)
      }
      if (t.mediaType === MediaType.IFRAME) {
        return selectIframeUrl(t)
      }
      if (t.mediaType === MediaType.INTERNAL_VIDEO) {
        return selectInternalVideoUrl(t)
      }
    },
    isImage: (t: MediaBizoType) =>
      t.mediaType === MediaType.IMAGE || t.mediaType === MediaType.LAYOUT,
    isVideo: (t: MediaBizoType) => t.mediaType === MediaType.VIDEO,
    isIframe: (t: MediaBizoType) => t.mediaType === MediaType.IFRAME,
    isReel: (t: MediaBizoType) =>
      t.mediaType === MediaType.INTERNAL_VIDEO &&
      t.originUrl?.startsWith(
        'https://s3.us-west-2.amazonaws.com/remotionlambda-uswest2-30tewi8y5c/'
      ),
    path: (t: MediaBizoType) => mediaUrl(t.mediaId),
    url: (t: MediaBizoType) => {
      if (t.mediaType === MediaType.VIDEO) {
        return selectVideoMaxishResImage(t) ?? staticUrl('img/nopics.svg')
      }
      if (t.mediaType === MediaType.IFRAME) {
        return selectIframeMaxResImage(t) ?? staticUrl('img/nopics.svg')
      }
      if (t.mediaType === MediaType.INTERNAL_VIDEO) {
        return selectInternalVideoImage(t) ?? staticUrl('img/nopics.svg')
      }

      return mediaUrl(t.mediaId, Size.XLARGE)
    },
  }
)
